// ============================================================
// calendarScopePref.ts
// Per-user, per-device preference: whether the Calendar/Diary shows
// MINE (my tasks + deadlines on cases assigned to me) or FIRM-WIDE
// (everything I'm permitted to see). Frontend-only, persisted in
// localStorage — same pattern as the calendar dark-focus + zoom prefs,
// backed by a tiny zustand store so the toggle button and the fetch
// both react to changes live.
//
// DEFAULT: 'mine' — the calendar reads as the user's own diary first;
// they can widen to firm-wide with the toggle. (This never widens task
// visibility beyond permissions — a non-admin in 'firm' still sees only
// their own tasks; the backend enforces that.)
// ============================================================
import { create } from 'zustand'

export type CalendarScope = 'mine' | 'firm'

const STORAGE_KEY = 'casevo_calendar_scope'

const readScope = (): CalendarScope => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'firm' ? 'firm' : 'mine'
  } catch {
    return 'mine'
  }
}

interface CalendarScopeState {
  scope: CalendarScope
  setScope: (s: CalendarScope) => void
}

export const useCalendarScopePref = create<CalendarScopeState>((set) => ({
  scope: readScope(),
  setScope: (s: CalendarScope) => {
    try {
      localStorage.setItem(STORAGE_KEY, s)
    } catch {
      /* storage blocked (private mode) — keep the in-memory value */
    }
    set({ scope: s })
  },
}))
